import { Stack, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { InventoryItem, TargetInventoryItem } from "store/api/generatedApi";
import { TargetInventoryDependencies } from "./dependencies";
import { usePeriod } from "hooks/search";
import { minimumPeriod } from "hooks/periodIndex";
import { usePeriodNameFormatter } from "hooks/usePeriodNameFormatter";
import { mapLoadedUnpack } from "models/loaded";

import {
  HotkeyCompliantDataGrid,
  NumberCell,
  getSortDataGridByMaterial,
} from "src/components/common/dataGrid";
import { MaterialPropertyViewCell } from "../common/MaterialPropertyViewCell";
import { useHandleTableMutations } from "./useHandleTableMutations";

export type TargetInventoryRow = TargetInventoryItem & {
  material_name: string;
  current_quantity: number | null;
};

export const TargetInventoryTable = ({
  materials,
  targetInventory,
  currentInventory,
}: TargetInventoryDependencies) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);
  const formatPeriodName = usePeriodNameFormatter();
  const [rootPeriod] = usePeriod(minimumPeriod);
  const rootPeriodName = mapLoadedUnpack(rootPeriod, (p) =>
    formatPeriodName(minimumPeriod, {
      name: p.name,
      startTimestamp: p.start_timestamp,
      endTimestamp: p.end_timestamp,
    })
  );

  const { materialNameColumnSortComparator, materialNameSortModel } =
    getSortDataGridByMaterial(materials);

  const { handleUpdateItem } = useHandleTableMutations();

  const rows = materials.byIndex.map((material) => {
    const item: TargetInventoryItem = targetInventory.find(
      (item) => item.material_id === material.id
    ) ?? { material_id: material.id, min_quantity: null, max_quantity: null };
    const current: InventoryItem | undefined = currentInventory.find(
      (c: InventoryItem) => c.material_id === material.id
    );
    return {
      ...item,
      material_name: material.name,
      current_quantity: current?.quantity ?? null,
    };
  });

  const columns: GridColDef<TargetInventoryRow>[] = [
    {
      field: "material_name",
      headerName: t("material"),
      sortable: true,
      sortComparator: materialNameColumnSortComparator,
      flex: 1,
      renderCell: (params) => (
        <MaterialPropertyViewCell
          name={params.row.material_name}
          id={params.row.material_id}
        />
      ),
      display: "flex",
    },
    {
      field: "min_quantity",
      headerName: `${t("targetMin")}${units("mass")}`,
      sortable: false,
      editable: true,
      flex: 1,
      renderCell: (params) => <NumberCell value={params.row.min_quantity} />,
      display: "flex",
    },
    {
      field: "max_quantity",
      headerName: `${t("targetMax")}${units("mass")}`,
      sortable: false,
      editable: true,
      flex: 1,
      renderCell: (params) => <NumberCell value={params.row.max_quantity} />,
      display: "flex",
    },
    {
      field: "current_quantity",
      headerName: `${t("currentInventory")}${units("mass")}${
        rootPeriodName ? ` [${rootPeriodName}]` : ""
      }`,
      sortable: false,
      editable: false,
      flex: 1,
      renderCell: (params) => (
        <NumberCell value={params.row.current_quantity} />
      ),
      display: "flex",
    },
  ];

  return (
    <Stack
      sx={{ maxHeight: "100%" }}
      // Prevent the modal from closing when pressing Escape key while in edit mode in the data grid
      onKeyDown={(e) => {
        if (e.key === "Escape") e.stopPropagation();
      }}
    >
      <HotkeyCompliantDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.material_id}
        processRowUpdate={handleUpdateItem}
        sx={{
          maxHeight: "100%",
          "& .MuiDataGrid-cell--editing input": {
            textAlign: "right",
            paddingX: 1,
            ...theme.typography.body1Mono,
          },
        }}
        initialState={{ sorting: materialNameSortModel }}
        cellSelection
        ignoreValueFormatterDuringExport
      />
    </Stack>
  );
};
